/**
 * Switch User Banner
 *
 * PE-762: Sticky banner shown when admin is viewing as another user.
 * Provides visual indication and easy switch-back functionality.
 */

import React from 'react';
import { useSwitchUser } from '../../contexts/SwitchUserContext';
import { AlertTriangle, User, ArrowLeft, Eye } from 'lucide-react';

export const SwitchUserBanner: React.FC = () => {
  const { isSwitched, originalUser, currentUser, switchBack } = useSwitchUser();

  if (!isSwitched || !originalUser || !currentUser) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-yellow-500 to-amber-500 border-b-2 border-yellow-600 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-2.5">
        <div className="flex items-center justify-between">
          {/* Left side - Warning and user info */}
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 p-1.5 bg-yellow-600/30 rounded-full">
              <Eye className="h-5 w-5 text-yellow-900" />
            </div>

            <div className="flex items-center gap-2 text-yellow-900">
              <span className="font-semibold text-sm">Viewing as:</span>
              <div className="flex items-center gap-2 px-3 py-1 bg-yellow-400/60 rounded-md border border-yellow-600/30">
                <User className="h-4 w-4" />
                <span className="font-bold">{currentUser.name}</span>
                <span className="text-xs px-1.5 py-0.5 bg-yellow-600/30 rounded">
                  {currentUser.role}
                </span>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-2 text-yellow-900 text-sm">
              <span className="text-yellow-700">|</span>
              <span>Logged in as:</span>
              <span className="font-medium">{originalUser.name}</span>
            </div>
          </div>

          {/* Right side - Switch Back Button */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1.5 text-yellow-900/80 text-xs">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>Actions will use their permissions</span>
            </div>

            <button
              onClick={switchBack}
              className="flex items-center gap-2 px-3 py-1.5 bg-white hover:bg-gray-50
                         text-yellow-900 text-sm font-medium rounded-md border border-yellow-600
                         shadow-sm transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Switch Back to Admin</span>
              <span className="sm:hidden">Switch Back</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwitchUserBanner;
